<template>
  <WidgetWrapper
    lg="6"
    :title="$t('delivery.dashboard.topCustomer', { count: dataShown })"
    :tooltip-text="$t('delivery.dashboard.topCustomerTooltip')"
  >
    <template #afterTitle>
      <div class="d-flex gap-3 flex-wrap customer-filter">
        <FormulateInput
          type="date-picker"
          v-model="period"
          format="DD MMM YYYY"
          value-type="YYYY-MM-DDTHH:mm:ssZ"
          range-separator=" - "
          range
          :placeholder="$t('global.action.selectDate')"
          :lang="datePickerLang"
          @input="handleChangePeriod"
          :confirm="true"
          :draft="true"
          :cancel="true"
          :confirmText="$t('global.apply')"
          :shortcuts="datepickerShorcuts"
        ></FormulateInput>
        <b-form-radio-group
          id="btn-radios-1"
          v-model="selected"
          :options="radioGroupOptions"
          name="radios-btn-default"
          buttons
          button-variant="outline-primary"
        ></b-form-radio-group>
      </div>
    </template>
    <b-dropdown
      id="actions-dropdown"
      toggle-tag="div"
      toggle-class="p-0 bg-transparent"
      class="w-auto mb-4"
    >
      <template #button-content>
        <McButton
          variant="secondary"
          icon="icon-outline_chevron-down"
          icon-position="right"
          :text="$t('delivery.dashboard.topData', { count: dataShown })"
        />
      </template>

      <b-dropdown-item
        class="mc-dashboard__order-dropdown"
        v-for="limit in dataLimits"
        :key="limit"
        @click="handleChangeDataShown(limit)"
        :class="{ 'font-weight-bold': limit === dataShown }"
        >{{ $t('delivery.dashboard.topData', { count: limit }) }}</b-dropdown-item
      >
    </b-dropdown>
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-skeleton-img no-aspect height="400px" />
      </template>
      <b-table
        v-if="selected === 'tabel'"
        sticky-header="400px"
        small
        show-empty
        :fields="fields"
        :items="customerList"
        responsive
        empty-table
        :empty-text="$t('global.emptyTable')"
      />
      <HorizontalBarChart v-else :options="chartOptions" :labels="labels" :datasets="datasets" />
    </b-skeleton-wrapper>
  </WidgetWrapper>
</template>

<script>
import WidgetWrapper from './WidgetWrapper.vue'
import McButton from '@/components/McButton'
import HorizontalBarChart from '@/components/Chart/HorizontalBar'

import topCustomerWidgetFields from '../lib/fields/topCustomerWidgetFields'
import datePickerLang from '../lib/constants/date-picker-lang'

import { getTopCustomerListWidget } from '@/services/rest-api/lib/dashboard/delivery-widget'
import { formatEndDateFilter } from '../helpers/formatTime'

export default {
  components: {
    WidgetWrapper,
    McButton,
    HorizontalBarChart,
  },
  data() {
    return {
      period: [],
      selected: 'tabel',
      dataShown: 5,
      loading: false,
      customerList: [],
    }
  },
  computed: {
    datePickerLang() {
      return datePickerLang()
    },
    fields() {
      return topCustomerWidgetFields()
    },
    datasets() {
      return [
        {
          data: this.customerList.map((customer) => customer.delivery_order_count),
          label: this.$t('delivery.fields.totalDelivered'),
          backgroundColor: '#1C74DA',
        },
      ]
    },
    labels() {
      return this.customerList.map((customer) => customer.customer.name)
    },
    dataLimits() {
      return [5, 10, 20]
    },
    apiClient() {
      return { get: getTopCustomerListWidget }
    },
    chartOptions() {
      return {
        legend: { position: 'bottom', labels: { boxWidth: 0 } },
        scales: {
          xAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  if (Number.isInteger(value)) {
                    return value
                  }
                },
              },
            },
          ],
        },
      }
    },
    radioGroupOptions() {
      return [
        {
          html: `<i class="icon-outline_browser"></i><span>${this.$t(
            'delivery.text.table'
          )}</span>`,
          value: 'tabel',
        },
        {
          html: `<i class="icon-outline_bar-chart-2"></i><span>${this.$t(
            'delivery.text.chart'
          )}</span>`,
          value: 'grafik',
        },
      ]
    },
    datepickerShorcuts() {
      return [
        {
          text: this.$t('global.time.today'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.yesterday'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 1)
            startDate.setHours(0, 0, 0)

            endDate.setDate(endDate.getDate() - 1)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.last7Days'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 7)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.last30Days'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 30)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.thisMonth'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(1)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.lastMonth'),
          onClick: () => {
            const startDate = new Date()
            startDate.setTime(startDate.getTime() - 3600 * 1000 * 24 * startDate.getDate())
            startDate.setTime(startDate.getTime() - 3600 * 1000 * 24 * (startDate.getDate() - 1))
            startDate.setHours(0, 0, 0)

            const endDate = new Date()
            endDate.setTime(endDate.getTime() - 3600 * 1000 * 24 * endDate.getDate())
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
      ].map((shortcut) => ({ ...shortcut, value: shortcut.onClick }))
    },
  },
  mounted() {
    this.handleFetchTopCustomerList()
  },
  methods: {
    handleChangeDataShown(count) {
      this.dataShown = count
      this.handleFetchTopCustomerList()
    },
    handleChangePeriod() {
      this.handleFetchTopCustomerList()
    },
    async handleFetchTopCustomerList() {
      this.loading = true

      const params = {
        limit: this.dataShown,
      }
      if (this.period.length > 0 && this.period.every((period) => period)) {
        params['filters'] = `action_on between (${this.period[0]} ${formatEndDateFilter(
          this.period[1]
        )})`
      }
      const res = await getTopCustomerListWidget(params)

      if (res.errors) return

      this.customerList = res.data.map((customer, idx) => ({ ...customer, number: `${idx + 1}.` }))
      this.loading = false
    },
  },
}
</script>

<style scoped lang="scss">
:deep(input[type='radio']) {
  display: none;

  & + span {
    display: flex;

    i {
      margin-right: 4px;
      font-size: 20px;
    }
  }
}

:deep(.mx-datepicker) {
  width: 215px !important;

  @include xs-screen-only {
    width: 100% !important;
  }
}

:deep(.formulate-input) {
  height: auto;
  padding: 0;
}

.customer-filter {
  margin-left: auto;
  flex-wrap: wrap;
  justify-content: end;
  width: 100%;

  @include xs-screen-only {
    flex-grow: 1;
    justify-content: center;

    & > * {
      width: 100% !important;
    }
  }
}

:deep(#actions-dropdown) {
  & > * {
    width: 150px !important;
  }

  .dropdown-menu {
    min-width: 0;
  }

  button {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
